/**
 * syncLock - Lock advisory compartido entre la app y el Service Worker
 *
 * Resuelve la race condition: tanto SyncManager (app) como sw.ts leen/escriben
 * la MISMA tabla `pending_changes` de `SyncDB` sin coordinación. Este lock usa
 * la tabla `sync_metadata` (compartida por ambos contextos porque apuntan al
 * mismo `SyncDB`) como semáforo, para que solo uno procese la cola a la vez.
 *
 * Es PURO: opera sobre una abstracción mínima (`MetaTable`) vinculada a la
 * clave `syncLock`. La app la construye envolviendo `adapter.getSyncMetadata/
 * setSyncMetadata`; el SW envolviendo `syncDd.table('sync_metadata')`. Ninguno
 * acopla a `window` ni al cliente Supabase.
 *
 * Modelo: lock con TTL. Si un contexto crashea manteniendo el lock, el otro lo
 * "roba" automáticamente al expirar (no requiere liberación manual para
 * recuperarse).
 */

export type LockOwner = 'app' | 'sw';

/** Payload del lock almacenado en sync_metadata['syncLock']. */
export interface SyncLockPayload {
  owner: LockOwner;
  acquiredAt: number;
  expiresAt: number;
}

/**
 * Abstracción vinculada a la entrada `syncLock` de sync_metadata.
 * Cada contexto la construye a su manera (adapter Dexie o tabla Dexie cruda).
 */
export interface MetaTable {
  get(): Promise<SyncLockPayload | undefined>;
  put(payload: SyncLockPayload): Promise<any>;
  remove?(): Promise<any>;
}

export const SYNC_LOCK_KEY = 'syncLock';

/**
 * Intenta adquirir el lock de sincronización.
 *
 * @returns `true` si el caller obtuvo el lock (o ya lo tenía y lo renueva);
 *          `false` si la cola está siendo procesada por el otro contexto con
 *          un lock aún vigente.
 */
export async function acquireSyncLock(
  meta: MetaTable,
  owner: LockOwner,
  ttlMs: number
): Promise<boolean> {
  const now = Date.now();
  const existing = await meta.get();

  // Reentrancia: si YA somos dueños (lock vigente), renovar y continuar.
  if (existing && existing.owner === owner && existing.expiresAt > now) {
    await meta.put({ owner, acquiredAt: now, expiresAt: now + ttlMs });
    return true;
  }

  // Si hay un lock vigente de otro contexto, esperar.
  if (existing && existing.expiresAt > now) {
    return false;
  }

  // Lock ausente o expirado: tomarlo.
  await meta.put({ owner, acquiredAt: now, expiresAt: now + ttlMs });
  return true;
}

/**
 * Libera el lock SOLO si el caller es el dueño actual.
 * Evita que un contexto libere el lock que otro acaba de tomar (tras expiración).
 */
export async function releaseSyncLock(meta: MetaTable, owner: LockOwner): Promise<void> {
  const existing = await meta.get();
  if (existing && existing.owner === owner) {
    if (typeof meta.remove === 'function') {
      await meta.remove();
    } else {
      // Fallback: sobrescribir con un lock ya expirado para que cualquiera lo tome.
      await meta.put({ owner, acquiredAt: 0, expiresAt: 0 });
    }
  }
}
